import type { CancelToken } from 'axios';
import Axios from 'axios';
import { neverCancel } from './async';
import type { TaskPriority } from './taskPriority';
import { makePriority } from './prioritySemaphore';
import type { Executor, RollbackHandler, TaskContext, TransientErrorHandler } from './taskContext';
import type { Clock, TimeoutCookie } from '../types/clock';
import { portableClock } from './timing';

export interface TaskScopeOptions {
  parent?: TaskContext;
  timeLimitMs?: number;
  cancelToken?: CancelToken;
  transientErrorHandler?: TransientErrorHandler;
  resilience?: Executor;
  priority?: TaskPriority;
  clock?: Clock;
  onRollback?: RollbackHandler;
  neverCancel?: boolean;
}

class RunDirectly implements Executor {
  run<T>(_: TaskContext, task: () => Promise<T>): Promise<T> {
    return task();
  }
}

export class TaskScope implements TaskContext {
  readonly transientErrorHandlers: TransientErrorHandler[];
  readonly cancelToken: CancelToken | null;
  readonly priority: TaskPriority;
  readonly resilience: Executor;
  readonly clock: Clock;
  readonly deadline: number;
  readonly onRollback: RollbackHandler;
  private timeoutCookie: TimeoutCookie | null = null;

  constructor(options: TaskScopeOptions) {
    const parent = options.parent;
    this.transientErrorHandlers = [...(parent?.transientErrorHandlers ?? []), ...(options.transientErrorHandler ? [options.transientErrorHandler] : [])];
    this.clock = options.clock ?? parent?.clock ?? portableClock();
    const ownDeadline = options.timeLimitMs ? (this.clock.monotonicNow() + options.timeLimitMs) : Infinity;
    this.deadline = Math.min(parent?.deadline ?? Infinity, ownDeadline);
    this.priority = options.priority ?? parent?.priority ?? makePriority();
    this.resilience = options.resilience ?? parent?.resilience ?? new RunDirectly();

    // Note: Rollback handlers are not inherited from parent, since they are typically scoped to running a local subtask.
    this.onRollback = options.onRollback ?? (() => {});

    let ct: CancelToken | null = null;
    if (options.neverCancel != true) {
      let createCt = false;
      if (parent?.cancelToken) {
        ct = parent.cancelToken;
      }

      if (options.cancelToken) {
        if (ct) {
          createCt = true;
        } else {
          ct = options.cancelToken;
        }
      }

      const newDeadline = options.timeLimitMs && this.deadline != parent?.deadline;
      if (newDeadline) {
        createCt = true;
      }

      if (createCt) {
        const cts = Axios.CancelToken.source();
        if (parent?.cancelToken) {
          void parent?.cancelToken.promise.then(cause => cts.cancel(cause.message));
        }
        if (options.cancelToken) {
          void options.cancelToken.promise.then(cause => cts.cancel(cause.message));
        }
        if (newDeadline) {
          if (this.timeoutCookie != null) {
            this.clock.clearTimeout(this.timeoutCookie);
          }
          this.timeoutCookie = this.clock.setTimeout(() => {
            cts.cancel('timeout');
          }, options.timeLimitMs!);
        }
        ct = cts.token;
      }
    }

    this.cancelToken = ct;
  }

  getCancelToken() {
    return this.cancelToken ?? neverCancel;
  }

  close() {
    if (this.timeoutCookie != null) {
      this.clock.clearTimeout(this.timeoutCookie);
      this.timeoutCookie = null;
    }
  }
}

export async function runTask<T>(opts: TaskScopeOptions, task: (scope: TaskScope) => Promise<T>) {
  const scope = new TaskScope(opts);
  try {
    return await scope.resilience.run(scope, () => task(scope));
  } finally {
    scope.close();
  }
}
